import { useMemo, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus, ShieldCheck, Trash2, UserCog, UserPlus } from "lucide-react";
import { toast } from "sonner";
import { AdminPageHeader } from "@/components/admin/AdminPageHeader";
import { AdminDataToolbar } from "@/components/admin/import/AdminDataToolbar";
import { DataTable, KpiCard, Panel, num, type DataColumn } from "@/components/admin/analytics/AnalyticsKit";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  activateAccount,
  createUserAccount,
  grantAdminRole,
  listAdminUsers,
  resendAccountConfirmation,
  revokeAdminRole,
  type AccountRow,
  type AdminUserRow,
  type AppRole,
} from "@/lib/admin/users.functions";

export const Route = createFileRoute("/_admin/admin/users")({
  head: () => ({
    meta: [
      { title: "Users & Roles — Admin" },
      {
        name: "description",
        content: "Add users, manage Admin and Super Admin roles, and review sign-in accounts.",
      },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: UsersRolesPage,
});

const ROLE_LABEL: Record<AppRole, string> = {
  admin: "Admin",
  super_admin: "Super Admin",
};

function formatDate(value: string | null): string {
  if (!value) return "—";
  return new Date(value).toLocaleString("en-IN", {
    dateStyle: "medium",
    timeStyle: "short",
  });
}

function UsersRolesPage() {
  const queryClient = useQueryClient();
  const listFn = useServerFn(listAdminUsers);
  const grantFn = useServerFn(grantAdminRole);
  const revokeFn = useServerFn(revokeAdminRole);
  const createFn = useServerFn(createUserAccount);
  const resendFn = useServerFn(resendAccountConfirmation);
  const activateFn = useServerFn(activateAccount);

  const query = useQuery({
    queryKey: ["admin", "users-roles"],
    queryFn: () => listFn(),
    staleTime: 15_000,
  });

  const [grantOpen, setGrantOpen] = useState(false);
  const [grantUserId, setGrantUserId] = useState<string>("");
  const [grantRole, setGrantRole] = useState<AppRole>("admin");
  const [confirmRevoke, setConfirmRevoke] = useState<AdminUserRow | null>(null);

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ["admin", "users-roles"] });

  const grant = useMutation({
    mutationFn: (v: { user_id: string; role: AppRole }) => grantFn({ data: v }),
    onSuccess: async () => {
      toast.success("Role granted");
      setGrantOpen(false);
      setGrantUserId("");
      setGrantRole("admin");
      await invalidate();
    },
    onError: (err: unknown) => {
      toast.error(err instanceof Error ? err.message : "Couldn't grant role.");
    },
  });

  const revoke = useMutation({
    mutationFn: (v: { user_id: string; role: AppRole }) => revokeFn({ data: v }),
    onSuccess: async () => {
      toast.success("Role revoked");
      setConfirmRevoke(null);
      await invalidate();
    },
    onError: (err: unknown) => {
      toast.error(err instanceof Error ? err.message : "Couldn't revoke role.");
    },
  });

  const users = useMemo(() => query.data?.users ?? [], [query.data]);
  const accounts = useMemo<AccountRow[]>(() => query.data?.accounts ?? [], [query.data]);
  const grantCandidates = useMemo(
    () => accounts.filter((a) => !a.roles.includes(grantRole)),
    [accounts, grantRole],
  );

  const [addOpen, setAddOpen] = useState(false);
  const [addEmail, setAddEmail] = useState("");
  const [addMode, setAddMode] = useState<"invite" | "create">("invite");
  const [addPassword, setAddPassword] = useState("");
  const [addRole, setAddRole] = useState<"none" | AppRole>("none");
  const [confirmActivate, setConfirmActivate] = useState<AccountRow | null>(null);

  const create = useMutation({
    mutationFn: (v: {
      email: string;
      mode: "invite" | "create";
      password?: string;
      role: "none" | AppRole;
    }) => createFn({ data: v }),
    onSuccess: async (_r, v) => {
      toast.success(v.mode === "invite" ? "Invitation sent" : "Account created");
      setAddOpen(false);
      setAddEmail("");
      setAddPassword("");
      setAddRole("none");
      setAddMode("invite");
      await invalidate();
    },
    onError: (err: unknown) => {
      toast.error(err instanceof Error ? err.message : "Couldn't add user.");
    },
  });

  const resend = useMutation({
    mutationFn: (v: { user_id: string }) => resendFn({ data: v }),
    onSuccess: () => toast.success("Confirmation email sent"),
    onError: (err: unknown) =>
      toast.error(err instanceof Error ? err.message : "Couldn't send email."),
  });

  const activate = useMutation({
    mutationFn: (v: { user_id: string }) => activateFn({ data: v }),
    onSuccess: async () => {
      toast.success("Account activated");
      setConfirmActivate(null);
      await invalidate();
    },
    onError: (err: unknown) =>
      toast.error(err instanceof Error ? err.message : "Couldn't activate account."),
  });

  const accountColumns: Array<DataColumn<AccountRow>> = [
    {
      key: "email",
      header: "Account",
      render: (r) => (
        <div>
          <p className="font-medium text-foreground">{r.email ?? "—"}</p>
          <p className="text-[11px] text-muted-foreground">{r.user_id}</p>
        </div>
      ),
      sortValue: (r) => r.email ?? "",
      exportValue: (r) => r.email ?? r.user_id,
    },
    {
      key: "type",
      header: "Type",
      render: (r) =>
        r.roles.length === 0 ? (
          <Badge variant="outline">Customer</Badge>
        ) : (
          <Badge variant="secondary">
            {r.roles.includes("super_admin") ? "Super Admin" : "Admin"}
          </Badge>
        ),
      exportValue: (r) => (r.roles.length ? r.roles.join(", ") : "customer"),
    },
    {
      key: "status",
      header: "Status",
      render: (r) =>
        r.confirmed ? (
          <span className="text-xs text-muted-foreground">Confirmed</span>
        ) : (
          <span className="text-xs font-medium text-destructive">Awaiting email confirmation</span>
        ),
      sortValue: (r) => (r.confirmed ? 1 : 0),
      exportValue: (r) => (r.confirmed ? "confirmed" : "unconfirmed"),
    },
    {
      key: "created",
      header: "Created",
      render: (r) => formatDate(r.created_at),
      sortValue: (r) => r.created_at,
      exportValue: (r) => r.created_at,
    },
    {
      key: "last_sign_in",
      header: "Last sign-in",
      render: (r) => formatDate(r.last_sign_in_at),
      sortValue: (r) => r.last_sign_in_at ?? "",
      exportValue: (r) => r.last_sign_in_at ?? "",
    },
    {
      key: "actions",
      header: "Actions",
      align: "right",
      render: (r) =>
        r.confirmed ? null : (
          <div className="flex justify-end gap-2">
            <Button
              size="sm"
              variant="outline"
              className="h-8"
              disabled={resend.isPending}
              onClick={() => resend.mutate({ user_id: r.user_id })}
            >
              Resend email
            </Button>
            <Button size="sm" className="h-8" onClick={() => setConfirmActivate(r)}>
              Activate
            </Button>
          </div>
        ),
    },
  ];


  const kpi = useMemo(
    () => ({
      total: users.length,
      admins: users.filter((u) => u.role === "admin").length,
      superAdmins: users.filter((u) => u.role === "super_admin").length,
    }),
    [users],
  );

  const submitGrant = () => {
    if (!grantUserId) {
      toast.error("Pick a user first.");
      return;
    }
    grant.mutate({ user_id: grantUserId, role: grantRole });
  };

  const columns: Array<DataColumn<AdminUserRow>> = [
    {
      key: "email",
      header: "User",
      render: (r) => (
        <div>
          <p className="font-medium text-foreground">{r.email ?? "—"}</p>
          <p className="text-[11px] text-muted-foreground">{r.user_id}</p>
        </div>
      ),
      sortValue: (r) => r.email ?? "",
      exportValue: (r) => r.email ?? r.user_id,
    },
    {
      key: "role",
      header: "Role",
      render: (r) => (
        <Badge variant={r.role === "super_admin" ? "default" : "secondary"}>
          {ROLE_LABEL[r.role]}
        </Badge>
      ),
      exportValue: (r) => ROLE_LABEL[r.role],
    },
    {
      key: "created",
      header: "Granted",
      render: (r) => formatDate(r.created_at),
      sortValue: (r) => r.created_at,
      exportValue: (r) => r.created_at,
    },
    {
      key: "last_sign_in",
      header: "Last sign-in",
      render: (r) => formatDate(r.last_sign_in_at),
      sortValue: (r) => r.last_sign_in_at ?? "",
      exportValue: (r) => r.last_sign_in_at ?? "",
    },
    {
      key: "actions",
      header: "Actions",
      align: "right",
      render: (r) => (
        <div className="flex justify-end">
          <Button
            size="sm"
            variant="outline"
            className="h-8 gap-1 text-destructive"
            onClick={() => setConfirmRevoke(r)}
          >
            <Trash2 className="h-3.5 w-3.5" aria-hidden />
            Revoke
          </Button>
        </div>
      ),
    },
  ];

  return (
    <div>
      <AdminPageHeader
        title="Users & Roles"
        description="Manage who has Admin or Super Admin access to this dashboard."
        actions={
          <AdminDataToolbar
            exportName="users-roles"
            moduleId="user-roles"
            onImported={() => void invalidate()}
            rows={() =>
              (query.data?.users ?? []).map((u: AdminUserRow) => ({
                "User ID": u.user_id,
                Email: u.email ?? "",
                Role: u.role,
              }))
            }
          />
        }
      />


      <div className="mb-4 grid grid-cols-2 gap-3 md:grid-cols-4">
        <KpiCard
          label="Admin users"
          value={num(kpi.total)}
          tone="info"
          icon={UserCog}
          loading={query.isPending}
        />
        <KpiCard
          label="Admins"
          value={num(kpi.admins)}
          tone="success"
          icon={ShieldCheck}
          loading={query.isPending}
        />
        <KpiCard
          label="Super admins"
          value={num(kpi.superAdmins)}
          tone="purple"
          icon={ShieldCheck}
          loading={query.isPending}
        />
      </div>

      {query.isError && (
        <div
          role="alert"
          className="mb-4 rounded-md border border-destructive/40 bg-destructive/5 px-4 py-3 text-sm text-destructive"
        >
          Couldn't load users and roles:{" "}
          {query.error instanceof Error && query.error.message === "forbidden"
            ? "this account does not have admin access."
            : query.error instanceof Error
              ? query.error.message
              : "unknown error"}
          . The counts above are not accurate until this loads.
        </div>
      )}

      <Panel title="Roles" subtitle={`${users.length} staff role assignments`}>
        <div className="mb-3 flex justify-end gap-2">
          <Button size="sm" variant="outline" className="gap-1.5" onClick={() => setAddOpen(true)}>
            <UserPlus className="h-3.5 w-3.5" aria-hidden />
            Add user
          </Button>
          <Button size="sm" className="gap-1.5" onClick={() => setGrantOpen(true)}>
            <Plus className="h-3.5 w-3.5" aria-hidden />
            Grant role
          </Button>
        </div>
        <DataTable
          rows={users}
          columns={columns}
          loading={query.isPending}
          exportName="admin-users"
          pageSize={12}
          minWidth={600}
          searchOn={(r) => `${r.email ?? ""} ${r.user_id}`}
        />
      </Panel>

      <div className="mt-6">
        <Panel
          title="All accounts"
          subtitle={`${accounts.length} sign-in accounts (customers and staff)`}
        >
          <DataTable
            rows={accounts}
            columns={accountColumns}
            loading={query.isPending}
            exportName="accounts"
            pageSize={15}
            minWidth={700}
            searchOn={(r) => `${r.email ?? ""} ${r.user_id}`}
          />
        </Panel>
      </div>

      <Dialog open={addOpen} onOpenChange={(o) => (o ? null : setAddOpen(false))}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Add user</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label htmlFor="add-email" className="mb-1 block text-xs text-muted-foreground">
                Email
              </Label>
              <Input
                id="add-email"
                type="email"
                value={addEmail}
                onChange={(e) => setAddEmail(e.target.value)}
                autoComplete="off"
              />
            </div>
            <div>
              <Label className="mb-1 block text-xs text-muted-foreground">How</Label>
              <Select value={addMode} onValueChange={(v) => setAddMode(v as "invite" | "create")}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="invite">Send invitation email (they set their password)</SelectItem>
                  <SelectItem value="create">Create with a password (Super Admin only)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {addMode === "create" && (
              <div>
                <Label htmlFor="add-password" className="mb-1 block text-xs text-muted-foreground">
                  Initial password (min. 8 characters)
                </Label>
                <Input
                  id="add-password"
                  type="password"
                  value={addPassword}
                  onChange={(e) => setAddPassword(e.target.value)}
                  autoComplete="new-password"
                />
                <p className="mt-1 text-[11px] text-muted-foreground">
                  The account is ready to sign in immediately. Ask them to change the password
                  after first sign-in.
                </p>
              </div>
            )}
            <div>
              <Label className="mb-1 block text-xs text-muted-foreground">Access</Label>
              <Select
                value={addRole}
                onValueChange={(v) => setAddRole(v as "none" | AppRole)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Customer (no admin access)</SelectItem>
                  <SelectItem value="admin">Admin</SelectItem>
                  <SelectItem value="super_admin">Super Admin</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddOpen(false)}>
              Cancel
            </Button>
            <Button
              disabled={create.isPending}
              onClick={() =>
                create.mutate({
                  email: addEmail,
                  mode: addMode,
                  password: addMode === "create" ? addPassword : undefined,
                  role: addRole,
                })
              }
            >
              {create.isPending ? "Saving…" : addMode === "invite" ? "Send invitation" : "Create account"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog
        open={confirmActivate !== null}
        onOpenChange={(o) => (o ? null : setConfirmActivate(null))}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Activate {confirmActivate?.email ?? "this account"}?</AlertDialogTitle>
            <AlertDialogDescription>
              Only do this if you have confirmed with this person directly (for example by phone)
              that this email address is theirs. They will be able to sign in with the password they
              chose. Their password, orders and details are not changed.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => confirmActivate && activate.mutate({ user_id: confirmActivate.user_id })}
            >
              Activate account
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={grantOpen} onOpenChange={(o) => (o ? null : setGrantOpen(false))}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Grant role</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="mb-1 block text-xs font-medium text-muted-foreground">User</label>
              <Select value={grantUserId} onValueChange={setGrantUserId}>
                <SelectTrigger>
                  <SelectValue placeholder="Select a user" />
                </SelectTrigger>
                <SelectContent>
                  {grantCandidates.length === 0 ? (
                    <div className="px-3 py-2 text-xs text-muted-foreground">
                      No accounts without this role.
                    </div>
                  ) : (
                    grantCandidates.map((u) => (
                      <SelectItem key={u.user_id} value={u.user_id}>
                        {u.email ?? u.user_id}
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-muted-foreground">Role</label>
              <Select value={grantRole} onValueChange={(v) => setGrantRole(v as AppRole)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="admin">Admin</SelectItem>
                  <SelectItem value="super_admin">Super Admin</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setGrantOpen(false)}>
              Cancel
            </Button>
            <Button onClick={submitGrant} disabled={grant.isPending}>
              {grant.isPending ? "Saving…" : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog
        open={confirmRevoke !== null}
        onOpenChange={(o) => (o ? null : setConfirmRevoke(null))}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Revoke {confirmRevoke ? ROLE_LABEL[confirmRevoke.role] : ""} from{" "}
              {confirmRevoke?.email ?? "this user"}?
            </AlertDialogTitle>
            <AlertDialogDescription>
              They will lose access to the admin panel unless they hold another admin role.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() =>
                confirmRevoke &&
                revoke.mutate({ user_id: confirmRevoke.user_id, role: confirmRevoke.role })
              }
            >
              Revoke
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
